import { ProviderCard } from '@/components/discovery/provider-card';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { useDirectoryEntries, useSearchProviders } from '@/hooks/use-discovery';
import { citySlug, directoryPath } from '@/lib/seo/directory';
import { directoryMeta } from '@/lib/seo/profile-meta';
import { Ionicons } from '@expo/vector-icons';
import { Href } from 'expo-router';
import React, { useMemo } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { ChipLinks, FinalCta, Reveal, SectionTitle, SeoSection } from './seo-blocks';
import { SeoHero } from './seo-hero';
import { SeoPageShell } from './seo-page-shell';

interface DirectoryScreenProps {
  categorySlug: string;
  /** `sao-paulo-sp`; ausente na página da categoria no Brasil todo. */
  city?: string;
}

/**
 * Diretório público (`/profissionais/[categoria]` e `.../[cidade]`): lista
 * real de quem atende pela Controlapag naquela categoria e cidade. Só
 * combinações com prestadores suficientes entram no sitemap e ficam
 * indexáveis; o resto sai com noindex (thin content). O HTML para buscador
 * vem da função `api/directory-page.ts`; esta tela é o que a pessoa vê.
 */
export function DirectoryScreen({ categorySlug, city }: DirectoryScreenProps) {
  const { data: entries, isPending: entriesPending } = useDirectoryEntries();

  const categoryEntries = useMemo(
    () => (entries ?? []).filter((entry) => entry.categorySlug === categorySlug),
    [entries, categorySlug],
  );
  const categoryEntry = categoryEntries.find((entry) => !entry.state);
  const cityEntry = city
    ? categoryEntries.find(
        (entry) =>
          entry.state && entry.cityNormalized && citySlug(entry.cityNormalized, entry.state) === city,
      )
    : undefined;
  const cityEntries = categoryEntries.filter((entry) => entry.state && entry.cityNormalized);

  const entry = city ? cityEntry : categoryEntry;
  const label = categoryEntry?.categoryLabel ?? cityEntry?.categoryLabel ?? categorySlug;
  const place = cityEntry?.city ? `${cityEntry.city} (${cityEntry.state})` : null;

  const { data, isPending } = useSearchProviders(
    { uf: cityEntry?.state ?? null, city: cityEntry?.city ?? null },
    { categories: [categorySlug] },
  );
  const items = data?.pages.flatMap((page) => page.items) ?? [];

  // Mesmo título, descrição (com a contagem) e H1 da função da Vercel.
  const { title, description, h1: heading } = directoryMeta({
    categoryLabel: label,
    city: cityEntry?.city ?? null,
    state: cityEntry?.state ?? null,
    providers: entry?.providers ?? null,
  });
  const path = entry ? directoryPath(entry) : `/profissionais/${categorySlug}${city ? `/${city}` : ''}`;

  return (
    <SeoPageShell
      screenTitle={label}
      dockLabel={`Trabalha com ${label.toLowerCase()}?`}
      head={<SeoHead title={title} description={description} path={path} noindex={!entriesPending && !entry} />}
    >
      <SeoHero
        crumbs={[
          { label: 'Início', href: '/' },
          ...(place && categoryEntry ? [{ label, href: directoryPath(categoryEntry) as Href }] : []),
          { label: place ?? label },
        ]}
        title={heading}
        sub="Veja preços e horários livres e agende direto."
      />

      <SeoSection>
        {isPending ? (
          <View className="items-center py-16">
            <ActivityIndicator color="#FD5001" />
          </View>
        ) : items.length === 0 ? (
          <View className="mx-auto w-full max-w-[680px] items-center gap-4 rounded-2xl border border-neutral-900/5 bg-card p-8">
            <Ionicons name="search-outline" size={28} color="#FD5001" />
            <ThemedText className="text-center font-semibold text-base">
              Ainda não há profissionais públicos aqui.
            </ThemedText>
            <ChipLinks items={[{ label: 'Buscar em outras cidades', href: '/buscar' as Href }]} />
          </View>
        ) : (
          <View className="flex-row flex-wrap gap-3">
            {items.map((card, index) => (
              <Reveal key={card.providerId} delay={(index % 3) * 60} className="w-[48%] grow basis-[40%] md:basis-[30%] md:grow-0">
                <ProviderCard card={card} href={`/@${card.slug}`} />
              </Reveal>
            ))}
          </View>
        )}

        {!city && cityEntries.length > 0 ? (
          <View className="mt-16">
            <SectionTitle title={`${label} por cidade`} />
            <ChipLinks
              items={cityEntries.map((item) => ({
                label: `${item.city} (${item.state})`,
                href: directoryPath(item) as Href,
              }))}
            />
          </View>
        ) : null}

        {city && categoryEntry ? (
          <View className="mt-12">
            <ChipLinks items={[{ label: `${label} em todo o Brasil`, href: directoryPath(categoryEntry) as Href }]} />
          </View>
        ) : null}
      </SeoSection>

      <FinalCta title={`Trabalha com ${label.toLowerCase()}? Crie sua conta grátis e conheça`} />
    </SeoPageShell>
  );
}
